import React, { useCallback, useEffect, useRef, useState } from "react";
import { ADMIN_API } from "../../../common/common";
import { getImageUrl } from "../../../common/Funcation";
import { notifyError, notifyMessage, postApi } from "../../../service";

export type CatalogGame = {
  _id?: string;
  gameCode: string;
  gameName: string;
  gameType?: string;
  platform?: string;
  sheetTab?: string;
  iconUrl?: string;
  hasSpacesImage?: boolean;
  spacesImage?: string;
  alreadyOnWebsite?: boolean;
  imageStatus?: string;
  previewImage?: string;
};

type Props = {
  websiteId: string;
  onPick: (game: CatalogGame, copiedImage?: string) => void;
  disabled?: boolean;
};

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  on_website: { label: "On site", color: "#0f5132", bg: "#d1e7dd" },
  has_spaces: { label: "Has image", color: "#084298", bg: "#cfe2ff" },
  sheet_icon: { label: "Sheet icon", color: "#664d03", bg: "#fff3cd" },
  no_image: { label: "No image", color: "#41464b", bg: "#e2e3e5" },
};

function GameCatalogPicker({ websiteId, onPick, disabled }: Props) {
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [gameType, setGameType] = useState("");
  const [imageStatus, setImageStatus] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<CatalogGame[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [gameTypes, setGameTypes] = useState<string[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pickingCode, setPickingCode] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const response = await postApi({
        api: ADMIN_API.SETTING.DASHBOARD.CATALOG_STATUS,
        value: {},
      });
      const data = response?.data?.data || {};
      setPlatforms(data.platforms || []);
      setGameTypes(data.gameTypes || []);
      setLastSyncedAt(data.lastSyncedAt || null);
    } catch (err: any) {
      console.error(err);
    }
  }, []);

  const search = useCallback(async () => {
    if (!websiteId) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await postApi({
        api: ADMIN_API.SETTING.DASHBOARD.CATALOG_SEARCH,
        value: {
          q: debouncedQ,
          platform,
          gameType,
          imageStatus,
          websiteId,
          page,
          limit: 24,
        },
      });
      const data = response?.data?.data || {};
      setResults(data.results || []);
      setTotalPages(data.totalPages || 0);
      setTotalResults(data.totalResults || 0);
    } catch (err: any) {
      notifyError(
        err?.response?.data?.message || "Failed to search game catalog",
      );
    } finally {
      setLoading(false);
    }
  }, [
    websiteId,
    debouncedQ,
    platform,
    gameType,
    imageStatus,
    page,
    refreshNonce,
  ]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    search();
  }, [search]);

  const onSearchChange = (value: string) => {
    setQ(value);
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(value);
    }, 350);
  };

  const handleSync = async () => {
    if (syncing) return;
    if (
      !window.confirm(
        "Pull the latest games from the AWC Google Sheet into the database? This can take 1–2 minutes.",
      )
    ) {
      return;
    }
    setSyncing(true);
    try {
      const response = await postApi({
        api: ADMIN_API.SETTING.DASHBOARD.CATALOG_SYNC,
        value: {},
        timeout: 180000,
      });
      const data = response?.data?.data || {};
      notifyMessage(
        `Catalog updated: ${data.totalActive || 0} active games (${data.tabsOk || 0}/${data.tabsTotal || 0} sheets).`,
      );
      await loadStatus();
      setPage(1);
      setRefreshNonce((n) => n + 1);
    } catch (err: any) {
      notifyError(
        err?.response?.data?.message || "Catalog sync failed. Try again.",
      );
    } finally {
      setSyncing(false);
    }
  };

  const handlePick = async (game: CatalogGame) => {
    if (disabled || pickingCode) return;
    setPickingCode(game.gameCode);
    try {
      let copiedImage = "";
      if (game.hasSpacesImage && game.spacesImage) {
        try {
          const response = await postApi({
            api: ADMIN_API.SETTING.DASHBOARD.CATALOG_COPY_IMAGE,
            value: {
              gameCode: game.gameCode,
              websiteId,
              sourceImage: game.spacesImage,
            },
          });
          copiedImage = response?.data?.data?.image || "";
        } catch (err: any) {
          notifyError(
            err?.response?.data?.message ||
              "Could not copy Spaces image. Fill other fields; upload image manually.",
          );
        }
      }
      onPick(game, copiedImage || undefined);
    } finally {
      setPickingCode("");
    }
  };

  const onDragStart = (e: React.DragEvent, game: CatalogGame) => {
    e.dataTransfer.setData(
      "application/x-catalog-game",
      JSON.stringify(game),
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  const formatSynced = (value: string | null) => {
    if (!value) return "Never";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 8,
        background: "#fff",
        height: "100%",
        minHeight: 640,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Game Catalog</div>
          <div style={{ fontSize: 12, color: "#6c757d", marginTop: 2 }}>
            Last sync: {formatSynced(lastSyncedAt)} · {totalResults} matches
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={syncing}
          onClick={handleSync}
          style={{ whiteSpace: "nowrap" }}
        >
          {syncing ? "Updating…" : "Update catalog"}
        </button>
      </div>

      <div style={{ padding: 12, borderBottom: "1px solid #eee" }}>
        <input
          className="form-control"
          placeholder="Search name or game code…"
          value={q}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <select
            className="form-control"
            value={platform}
            onChange={(e) => {
              setPlatform(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All platforms</option>
            {platforms.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            className="form-control"
            value={gameType}
            onChange={(e) => {
              setGameType(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All types</option>
            {gameTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <select
          className="form-control"
          value={imageStatus}
          onChange={(e) => {
            setImageStatus(e.target.value);
            setPage(1);
          }}
          style={{ marginTop: 8 }}
        >
          <option value="">All image statuses</option>
          <option value="hasSpaces">Has Spaces image</option>
          <option value="missing">Missing Spaces image</option>
          <option value="onWebsite">Already on this website</option>
          <option value="notOnWebsite">Not on this website</option>
        </select>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 10,
          background: "#f8f9fa",
        }}
      >
        {loading && (
          <div style={{ padding: 16, textAlign: "center", color: "#6c757d" }}>
            Loading…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", color: "#6c757d" }}>
            {lastSyncedAt
              ? "No games found. Try another search."
              : "Catalog is empty. Click “Update catalog” first."}
          </div>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {results.map((game) => {
            const status =
              STATUS_LABELS[game.imageStatus || "no_image"] ||
              STATUS_LABELS.no_image;
            const thumb = game.previewImage
              ? getImageUrl(game.previewImage)
              : "";
            const busy = pickingCode === game.gameCode;
            return (
              <div
                key={game.gameCode}
                draggable={!disabled && !busy}
                onDragStart={(e) => onDragStart(e, game)}
                onClick={() => handlePick(game)}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: 8,
                  background: "#fff",
                  border: "1px solid #e9ecef",
                  borderRadius: 6,
                  cursor: disabled || busy ? "wait" : "grab",
                  opacity: busy ? 0.6 : 1,
                  alignItems: "center",
                }}
                title="Click or drag to the form"
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 4,
                    overflow: "hidden",
                    background: "#e9ecef",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 11, color: "#adb5bd" }}>N/A</span>
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {game.gameName}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6c757d",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {game.platform} · {game.gameType} · {game.gameCode}
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "1px 6px",
                      borderRadius: 99,
                      color: status.color,
                      background: status.bg,
                    }}
                  >
                    {busy ? "Applying…" : status.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            padding: "8px 12px",
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span style={{ fontSize: 12, color: "#6c757d" }}>
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default GameCatalogPicker;
